import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { RuntimeConfig, RuntimeConfigService } from './runtime-config.service';

/**
 * Angular APP_INITIALIZER factory function that fetches runtime configuration before app starts.
 *
 * This initializer runs BEFORE the Purly initializer and fetches assets/runtime-config.json
 * using HttpClient. The config is then set on RuntimeConfigService for the Purly
 * initializer to read.
 *
 * ARCHITECTURE:
 * 1. This initializer runs first (registered before Purly initializer)
 * 2. Fetches assets/runtime-config.json using Angular HttpClient
 * 3. Sets config on RuntimeConfigService using dependency injection
 * 4. Purly initializer runs second and reads from RuntimeConfigService
 *
 * CONSUMING APPLICATION REQUIREMENTS:
 * - Create src/assets/runtime-config.json with valid Purly configuration
 * - Ensure assets directory is included in angular.json build configuration
 * - That's it! No script tags or additional setup required.
 *
 * ERROR HANDLING:
 * - Tries assets/runtime-config.json first; falls back to assets/purly-config.json
 *   (deprecated name) on any failure — including 404 and JSON parse errors
 * - If neither file is usable, the app continues without Purly
 * - The Purly initializer will gracefully skip initialization if config is unavailable
 *
 * @returns Factory function that returns a Promise for Angular's APP_INITIALIZER
 */
export function loadRuntimeConfig() {
  // Capture dependencies immediately in injection context
  let http: HttpClient;
  let logger: NGXLogger;
  let runtimeConfigService: RuntimeConfigService;

  try {
    http = inject(HttpClient);
    logger = inject(NGXLogger);
    runtimeConfigService = inject(RuntimeConfigService);
  } catch (error) {
    console.error('[RuntimeConfig] ❌ CRITICAL: Failed to inject dependencies:', error);
    throw error;
  }

  const noCacheHeaders = {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  };

  const fetchConfig = (path: string): Promise<RuntimeConfig> =>
    firstValueFrom(http.get<RuntimeConfig>(path, { headers: noCacheHeaders }));

  const applyConfig = (config: RuntimeConfig, path: string): void => {
    if (!config || !config.purly) {
      throw new Error('Invalid config structure: missing "purly" property');
    }

    if (!config.purly.baseUrl) {
      logger.warn('[RuntimeConfig] WARNING: baseUrl is missing');
    }

    if (!config.purly.apiKey) {
      logger.warn('[RuntimeConfig] WARNING: apiKey is empty');
      logger.warn('[RuntimeConfig] Purly service may fail to initialize');
    }

    runtimeConfigService.setConfig(config);

    logger.info(`[RuntimeConfig] ✅ Configuration loaded successfully from ${path}`);
    logger.trace('[RuntimeConfig] baseUrl:', config.purly.baseUrl);
    logger.trace('[RuntimeConfig] clientId:', config.purly.clientId);
    logger.trace('[RuntimeConfig] enabled:', config.purly.enabled);
  };

  return () => {
    const primaryPath = 'assets/runtime-config.json';
    const legacyPath = 'assets/purly-config.json';

    logger.info(`[RuntimeConfig] Loading configuration from ${primaryPath}`);

    return fetchConfig(primaryPath).then(
      (config) => applyConfig(config, primaryPath),
      (error: any) => {
        const is404 = error?.status === 404;

        if (is404) {
          logger.warn(`[RuntimeConfig] ${primaryPath} not found, trying legacy ${legacyPath}`);
          logger.warn('[RuntimeConfig] DEPRECATED: Rename assets/purly-config.json to assets/runtime-config.json');
        } else {
          logger.error(`[RuntimeConfig] Failed to load ${primaryPath}:`, error?.message || error);
          logger.warn(`[RuntimeConfig] Trying legacy ${legacyPath} as fallback`);
        }

        return fetchConfig(legacyPath).then(
          (config) => applyConfig(config, legacyPath),
          (legacyError: any) => {
            if (legacyError?.status === 404) {
              logger.warn(`[RuntimeConfig] Configuration file not found: ${primaryPath}`);
              logger.warn('[RuntimeConfig] Purly service will skip initialization');
              logger.info('[RuntimeConfig] To enable Purly:');
              logger.info('[RuntimeConfig]   1. Create src/assets/runtime-config.json');
              logger.info('[RuntimeConfig]   2. Add valid Purly configuration');
              logger.info('[RuntimeConfig]   3. Ensure src/assets is in angular.json assets array');
            } else {
              logger.error(
                `[RuntimeConfig] Failed to load ${legacyPath}:`,
                legacyError?.message || legacyError
              );
            }
            // Don't throw - allow app to continue without Purly
          }
        );
      }
    );
  };
}
